import React from "react";
import RowHeader from "./RowHeader";
import GridCell from "./GridCell";
import FilterButton from "./FilterButton";
import { DEFAULT_COL_WIDTH } from "../../constants";

/**
 * GridRow
 * One `<tr>` and all its cells, including the leftmost row-number TH.
 * Encapsulates per-row decisions: which cells are visible (hidden cols),
 * which cells are merge slaves (skipped), and the per-cell sticky style
 * computed from freeze offsets.
 */
// Shared immutable style for non-frozen cells — stable identity so
// React.memo(GridCell) sees an unchanged prop across renders.
const EMPTY_STICKY = Object.freeze({});

function GridRow({
  rowIndex, row, rowHeight, trClassName,
  // Cell data (cellStyles = border-processed map from GridBody)
  cellStyles, cellRichHtml, cellMerges, cellLinks,
  hiddenCols,
  // Selection state
  selectedCell, normalizedSelection, selectionType,
  // Hidden indicators
  isLastVisibleBeforeHidden,
  // Column metadata
  colWidths, maxCols,
  // Freeze pane state for this row
  isFrozenRow, isLastFrozenRow, frozenCols,
  measuredTheadHeight, frozenRowOffset, frozenColOffsets,
  // Selection / row handlers
  onSelectRow, onExtendRow, onHeaderContextMenu, onStartResize,
  onStartSelection, onExtendSelection,
  onLinkClick, onCellContextMenu,
  // Sort/Filter dropdowns — only provided for the filter row (row 0),
  // null for every other row so their cells stay memoized.
  filterCtx,
}) {
  const rowHeaderClass = [
    selectionType === "row" && rowIndex >= normalizedSelection.r1 && rowIndex <= normalizedSelection.r2
      ? "sev-sel-header" : "",
    isFrozenRow ? "sev-freeze-rowhdr" : "",
  ].join(" ").trim();

  const rowHeaderStyle = {
    height: `${rowHeight}px`,
    ...(isFrozenRow
      ? { position: "sticky", top: `${measuredTheadHeight + (frozenRowOffset || 0)}px`, left: 0, zIndex: 15 }
      : {}),
  };

  return (
    <tr
      key={`row-${rowIndex}`}
      className={[trClassName, isLastFrozenRow ? "sev-freeze-rowline" : ""].join(" ").trim()}
      style={{ height: `${rowHeight}px` }}
    >
      <RowHeader
        rowIndex={rowIndex}
        rowHeight={rowHeight}
        className={rowHeaderClass}
        style={rowHeaderStyle}
        onSelect={onSelectRow}
        onExtend={onExtendRow}
        onContextMenu={(e) => onHeaderContextMenu(e, "row", rowIndex)}
        onStartResize={onStartResize}
      />
      {Array.from({ length: maxCols }).map((_, colIndex) => {
        if (hiddenCols.has(colIndex)) return null;
        // Cells beyond the sheet's parsed data render empty — this is what
        // lets the grid keep growing columns (AA, AB, …) as the user
        // scrolls right, like Excel's endless grid.
        const cell = row[colIndex];
        const key = `${rowIndex}:${colIndex}`;
        const mergeInfo = cellMerges[key];
        if (mergeInfo === null) return null; // slave cell of a merged region

        // Compute effective row/col span respecting hidden rows/cols
        const mergeSpan = mergeInfo || {};
        let effectiveColSpan = mergeSpan.colSpan || 1;
        if (effectiveColSpan > 1) {
          let visibleSpan = 0;
          for (let ci = colIndex; ci < colIndex + effectiveColSpan; ci++) {
            if (!hiddenCols.has(ci)) visibleSpan++;
          }
          effectiveColSpan = Math.max(1, visibleSpan);
        }
        const effectiveRowSpan = mergeSpan.rowSpan || 1;

        // Border post-processing happens once per sheet in GridBody
        // (processedStyles) — here we just look the stable object up.
        // PERF: a per-render `{...spread}` here would give every cell a
        // new style identity and defeat React.memo(GridCell).
        const cleanCellStyle = cellStyles[key];

        const isFrozenCol = colIndex < frozenCols;
        const isLastFrozenCol = colIndex === frozenCols - 1;

        // Sticky style for frozen cells.  Frozen cells must have a solid
        // background so scrolled content beneath them does not show
        // through.  Non-frozen cells share ONE frozen empty object so
        // their prop identity never changes (React.memo).
        let stickyStyle = EMPTY_STICKY;
        if (isFrozenCol || isFrozenRow) {
          stickyStyle = {};
          if (isFrozenCol) {
            stickyStyle.position = "sticky";
            stickyStyle.left = `${frozenColOffsets[colIndex] || 0}px`;
            stickyStyle.zIndex = 6;
          }
          if (isFrozenRow) {
            stickyStyle.position = "sticky";
            stickyStyle.top = `${measuredTheadHeight + (frozenRowOffset || 0)}px`;
            stickyStyle.zIndex = isFrozenCol ? 8 : 7;
          }
          const bgColor = (cleanCellStyle && cleanCellStyle.backgroundColor) || "#ffffff";
          stickyStyle.backgroundColor = bgColor;
          stickyStyle["--cell-gap-shadow"] = `0 0 0 1.5px ${bgColor}`;
        }

        const inSelection =
          rowIndex >= normalizedSelection.r1 && rowIndex <= normalizedSelection.r2 &&
          colIndex >= normalizedSelection.c1 && colIndex <= normalizedSelection.c2;

        const className = [
          selectedCell.row === rowIndex && selectedCell.col === colIndex ? "sev-sel-active" : "",
          inSelection
            ? `sev-sel-range ${rowIndex === normalizedSelection.r1 ? "sev-sel-top" : ""} ${rowIndex === normalizedSelection.r2 ? "sev-sel-bottom" : ""} ${colIndex === normalizedSelection.c1 ? "sev-sel-left" : ""} ${colIndex === normalizedSelection.c2 ? "sev-sel-right" : ""}`
            : "",
          cellLinks[key] ? "sev-link-cell" : "",
          isLastVisibleBeforeHidden(colIndex) ? "sev-hidden-col-after" : "",
          (isFrozenRow || isFrozenCol) ? "sev-freeze-cell" : "",
          isLastFrozenCol ? "sev-freeze-colline" : "",
          isLastFrozenRow ? "sev-freeze-rowline" : "",
        ].join(" ").trim();

        const linkUrl = cellLinks[key];

        // Sort/Filter dropdown lives on the filter row's cells (Excel-style),
        // only on the columns the autofilter covers.
        const menuOpen = !!(filterCtx && filterCtx.activeFilterCol === colIndex);
        const filterButton = filterCtx && filterCtx.isFilterColumn(colIndex) ? (
          <FilterButton
            colIndex={colIndex}
            isFiltered={Boolean(filterCtx.filters[colIndex])}
            isMenuOpen={filterCtx.activeFilterCol === colIndex}
            rows={filterCtx.normalizedRows}
            currentFilter={filterCtx.filters[colIndex]}
            sortState={filterCtx.getSortState ? filterCtx.getSortState(colIndex) : null}
            onToggle={filterCtx.onToggleFilter}
            onApply={filterCtx.onApplyFilter}
            onClose={filterCtx.onCloseFilter}
            onSortAsc={filterCtx.onSortAsc}
            onSortDesc={filterCtx.onSortDesc}
            onClearSort={filterCtx.onClearSort}
            onCustomSort={filterCtx.onCustomSort}
          />
        ) : undefined;

        return (
          <GridCell
            key={`cell-${rowIndex}-${colIndex}`}
            rowIndex={rowIndex}
            colIndex={colIndex}
            cellValue={cell}
            html={cellRichHtml[key]}
            linkUrl={linkUrl}
            rowHeight={rowHeight}
            colWidth={colWidths[colIndex] || DEFAULT_COL_WIDTH}
            effectiveColSpan={effectiveColSpan}
            effectiveRowSpan={effectiveRowSpan}
            cellStyle={cleanCellStyle}
            stickyStyle={stickyStyle}
            className={className}
            filterButton={filterButton}
            bumpZ={menuOpen}
            onStartSelection={onStartSelection}
            onExtendSelection={onExtendSelection}
            onLinkClick={onLinkClick}
            onCellContextMenu={onCellContextMenu}
          />
        );
      })}
    </tr>
  );
}

export default React.memo(GridRow);
